import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress
} from '@mui/material';
import { ReportContext } from '../../Context';
import axios from 'axios';
import DescriptionIcon from '@mui/icons-material/Description';
import { Document, Packer, Paragraph, TextRun, AlignmentType, Header, BorderStyle, Table, TableRow, TableCell, WidthType, ImageRun } from 'docx';
import { saveAs } from 'file-saver';

function AttorneyWaiver() {
  const { report } = useContext(ReportContext);
  
  const [loading, setLoading] = useState(true);
  const [personnesPhysiques, setPersonnesPhysiques] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [settings, setSettings] = useState(null);
  
  const [personneEntendue, setPersonneEntendue] = useState('');
  const [personneEntendueData, setPersonneEntendueData] = useState(null);
  const [dateHeureRenonciation, setDateHeureRenonciation] = useState('');
  const [typeAudition, setTypeAudition] = useState('');

  // Types d'audition disponibles
  const typesAudition = [
    { value: 'S3', label: 'S3 - Suspect MAJEUR - Pas privé de liberté' },
    { value: 'S4_prive', label: 'S4 - Suspect MAJEUR - Privé de liberté' },
    { value: 'S4_inculpe', label: 'S4 - Inculpé - Sous mandat d\'arrêt' }
  ];

  // Helpers logo
  const svgTextToPngBase64 = (svgText, width = 150, height = 150) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        const base64 = canvas.toDataURL('image/png').split(',')[1];
        resolve(base64);
      };
      
      img.onerror = reject;
      
      const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      img.src = url;
    });
  };

  const base64ToUint8Array = (base64) => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  };

  const loadLogoPng = async () => {
    const svgContent = `<?xml version="1.0" encoding="utf-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 150 150" width="150" height="150">
  <circle cx="75" cy="75" r="74" fill="#FFFFFF" stroke="#244BA6" stroke-width="2"/>
  <path fill="#244BA6" d="M150,75c0-41.4-33.6-75-75-75S0,33.6,0,75c0,39.5,30.5,71.9,69.3,74.8c-3.3-4-7.4-9.5-11-14.6
    c-6.1-8.6-13.4-21.5-13.7-34.4c-0.2-9.6,2-14.8,14.6-34.7c4.9-7.8,8.2-21.1,7.7-23.9c-0.5-2.9,4.6,4.3,6.5,9.1
    c2.5,6.4,2.2,11.2-2.5,24.4c-2.2,6.3-5.9,16.4-2.2,20.9c-0.4-0.5,11.9-13.2,14.8-26.2c2.2-10,1.3-23.3-3-33.1
    c-3.8-8.6-8.3-15.7-7.4-27.1c0.4-4.8,1.7-9.4,1.8-9.7c2.8,7,4,9.5,5.1,11.5c5.1,9.6,15.6,23.7,20.5,33.4
    c5,9.6,8.8,25.5,3,41.7c-5.7,15.9-11.1,21.3-16.9,34.2c-4.3,9.6-7.9,21.4-9.7,28.8C117.5,149,150,115.9,150,75"/>
</svg>`;
    
    try {
      const base64 = await svgTextToPngBase64(svgContent, 100, 100);
      return base64ToUint8Array(base64);
    } catch (error) {
      console.warn('Erreur conversion logo:', error);
      return null;
    }
  };
  
  // Fonction pour construire le numéro de PV complet
  const buildReportNumber = () => {
    if (!reportData || !settings) return 'XXXXXX';
    
    const shortJudicialDistrict = settings.shortJudicialDistrict || '__';
    const indice = reportData.indice || '__';
    const judicialDivision = settings.judicialDivision || '__';
    const number = report?.number || '__';
    const year = report?.year || '__';
    
    return `${shortJudicialDistrict}.${indice}.${judicialDivision}.${number}/${year}`;
  };

  // Fonction pour formater la date en français long
  const formatDateLongMonth = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const mois = [
      'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
      'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
    ];
    const jour = String(date.getDate()).padStart(2, '0');
    const moisNom = mois[date.getMonth()];
    const annee = date.getFullYear();
    return `${jour} ${moisNom} ${annee}`;
  };

  // Fonction pour formater la date en JJ/MM/AAAA
  const formatDateShort = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const jour = String(date.getDate()).padStart(2, '0');
    const mois = String(date.getMonth() + 1).padStart(2, '0');
    const annee = date.getFullYear();
    return `${jour}/${mois}/${annee}`;
  };

  // Fonction pour extraire la date et l'heure d'un datetime-local
  const extractDateTimeParts = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const hours = date.getHours();
    const hoursStr = String(hours).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const hourLabel = hours >= 2 ? 'heures' : 'heure';
    return {
      dateText: formatDateLongMonth(value),
      timeText: `${hoursStr}:${minutes}`,
      hourLabel: hourLabel,
      dateObj: date
    };
  };
  
  // Charger les données au montage du composant
  useEffect(() => {
    const fetchData = async () => {
      if (!report || !report.number || !report.year) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const token = sessionStorage.getItem('authToken');
        
        // Récupérer les paramètres
        const settingsResponse = await axios.get('/api/settings');
        setSettings(settingsResponse.data);
        
        // Récupérer les données du rapport
        const reportResponse = await axios.get(
          `/api/report/search?number=${report.number}&year=${report.year}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setReportData(reportResponse.data);
        
        // Formater les personnes physiques (uniquement celles avec un nom)
        const personnesFormatted = (reportResponse.data.personnesPhysiques || [])
          .filter(p => p.lastname && !p.isUnknown)
          .map(p => ({
            value: `${p.lastname.toUpperCase()} ${p.firstname || ''}`,
            data: p
          }));
        
        setPersonnesPhysiques(personnesFormatted);
        
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [report]);
  
  // Gérer la sélection de la personne
  const handlePersonneChange = (e) => {
    const value = e.target.value;
    setPersonneEntendue(value);
    const selectedPersonne = personnesPhysiques.find(p => p.value === value);
    setPersonneEntendueData(selectedPersonne?.data || null);
  };
  
  // Vérifier si tous les champs sont remplis
  const isFormComplete = personneEntendue && dateHeureRenonciation && typeAudition;
  
  const handleGenerate = async () => {
    if (!isFormComplete || !reportData || !settings || !personneEntendueData) {
      console.error('Données manquantes pour générer le document');
      return;
    }
    
    try {
      const reportNumber = buildReportNumber();
      const logoImageData = await loadLogoPng();
      
      const fontFamily = 'Helvetica';
      const fontSize9pt = 18;
      const fontSize10pt = 20;
      const typeValue = settings.type === 'local' ? 'Locale' : settings.type === 'federal' ? 'Fédérale' : settings.type || 'N/A';
      
      const buildHeader = () => {
        const noBorders = {
          top: { style: BorderStyle.NONE },
          bottom: { style: BorderStyle.NONE },
          left: { style: BorderStyle.NONE },
          right: { style: BorderStyle.NONE },
        };
        
        const headerRows = [
          new TableRow({
            children: [
              new TableCell({
                width: { size: 12, type: WidthType.PERCENTAGE },
                borders: noBorders,
                children: [
                  new Paragraph({
                    children: logoImageData ? [
                      new ImageRun({
                        data: logoImageData,
                        transformation: { width: 40, height: 40 },
                        type: 'png',
                      })
                    ] : []
                  })
                ]
              }),
              new TableCell({
                width: { size: 38, type: WidthType.PERCENTAGE },
                borders: noBorders,
                children: [
                  new Paragraph({
                    spacing: { after: 40 },
                    children: [
                      new TextRun({
                        text: `Police ${typeValue}`,
                        font: fontFamily,
                        size: fontSize9pt,
                      }),
                    ],
                  }),
                  new Paragraph({
                    spacing: { after: 40 },
                    children: [
                      new TextRun({
                        text: `${settings.clientName || 'N/A'} - ${settings.zoneNumber || 'N/A'}`,
                        font: fontFamily,
                        size: fontSize9pt,
                        bold: true,
                      }),
                    ],
                  }),
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: `Service ${reportData.unit || ''}`,
                        font: fontFamily,
                        size: fontSize9pt,
                      }),
                    ],
                  }),
                ],
              }),
              new TableCell({
                width: { size: 50, type: WidthType.PERCENTAGE },
                borders: noBorders,
                children: [
                  new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    spacing: { after: 40 },
                    children: [
                      new TextRun({
                        text: `Procès-verbal ${reportData.reportType === "subsequent" ? "subséquent" : reportData.reportType}`,
                        font: fontFamily,
                        size: fontSize9pt,
                      }),
                    ],
                  }),
                  new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    spacing: { after: 40 },
                    children: [
                      new TextRun({
                        text: reportNumber,
                        font: fontFamily,
                        size: fontSize9pt,
                        bold: true,
                      }),
                    ],
                  }),
                  new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    children: [
                      new TextRun({
                        text: 'Annexe n° _____',
                        font: fontFamily,
                        size: fontSize9pt
                      }),
                    ],
                  }),
                ],
              }),
            ],
          }),
        ];
        
        const headerChildren = [
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
              top: { style: BorderStyle.NONE },
              bottom: { style: BorderStyle.NONE },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE },
              insideHorizontal: { style: BorderStyle.NONE },
              insideVertical: { style: BorderStyle.NONE },
            },
            rows: headerRows,
          }),
          new Paragraph({
            border: {
              bottom: {
                color: '000000',
                space: 1,
                style: BorderStyle.SINGLE,
                size: 6,
              },
            },
            spacing: { after: 200, before: 0, line: 20 },
            children: [
              new TextRun({
                text: '',
                size: 4,
              }),
            ],
          })
        ];
        
        return new Header({ children: headerChildren });
      };

      // Déterminer le genre de la personne
      const isFemale = personneEntendueData.gender === 'F';
      
      // Mots selon le genre
      const soussigne = isFemale ? 'soussignée' : 'soussigné';
      const inscrit = isFemale ? 'Inscrite' : 'Inscrit';
      const prive = isFemale ? 'privée' : 'privé';
      const entendu = isFemale ? 'entendue' : 'entendu';
      const conscient = isFemale ? 'consciente' : 'conscient';
      const contraint = isFemale ? 'contrainte' : 'contraint';
      
      // Construire l'identité
      const dateNaissance = personneEntendueData.birthdate ? 
        formatDateShort(personneEntendueData.birthdate) : '';
      const birthLine = dateNaissance ? `${dateNaissance}${personneEntendueData.birthPlace ? ` à ${personneEntendueData.birthPlace}` : ''}` : '';
      
      // Extraire date et heure de renonciation
      const dateTimeParts = extractDateTimeParts(dateHeureRenonciation);
      const dateRenonciation = dateTimeParts ? dateTimeParts.dateText : '';
      const heureRenonciation = dateTimeParts ? `${dateTimeParts.timeText} ${dateTimeParts.hourLabel}` : '';

      // Construire les paragraphes du corps selon le type d'audition
      const buildBodyParagraphs = () => {
        const paragraphs = [];
        
        // Date et heure en introduction
        paragraphs.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `Le ${dateRenonciation} à ${heureRenonciation},`,
                font: fontFamily,
                size: fontSize10pt
              })
            ]
          })
        );
        
        paragraphs.push(new Paragraph({ text: '' }));
        
        // Je soussigné(e)
        paragraphs.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `Je ${soussigne} :`,
                font: fontFamily,
                size: fontSize10pt
              })
            ]
          })
        );
        
        paragraphs.push(new Paragraph({ text: '' }));
        
        // Nom et prénom
        paragraphs.push(
          new Paragraph({
            children: [
              new TextRun({ text: 'Nom et prénom : ', font: fontFamily, size: fontSize10pt }),
              new TextRun({ 
                text: (personneEntendueData.lastname || '').toUpperCase(), 
                font: fontFamily, 
                size: fontSize10pt, 
                bold: true,
                underline: {}
              }),
              new TextRun({ text: ' ', font: fontFamily, size: fontSize10pt }),
              new TextRun({ 
                text: personneEntendueData.firstname || '', 
                font: fontFamily, 
                size: fontSize10pt, 
                bold: true
              })
            ]
          })
        );
        
        // Date et lieu de naissance
        if (birthLine) {
          paragraphs.push(
            new Paragraph({
              children: [
                new TextRun({ text: 'Date et lieu de naissance : ', font: fontFamily, size: fontSize10pt }),
                new TextRun({ text: birthLine, font: fontFamily, size: fontSize10pt, bold: true })
              ]
            })
          );
        }
        
        // Nationalité
        if (personneEntendueData.nationality) {
          paragraphs.push(
            new Paragraph({
              children: [
                new TextRun({ text: 'Nationalité : ', font: fontFamily, size: fontSize10pt }),
                new TextRun({ text: personneEntendueData.nationality, font: fontFamily, size: fontSize10pt, bold: true })
              ]
            })
          );
        }
        
        // Adresse
        if (personneEntendueData.residence) {
          paragraphs.push(
            new Paragraph({
              children: [
                new TextRun({ text: `${inscrit} à : `, font: fontFamily, size: fontSize10pt }),
                new TextRun({ text: personneEntendueData.residence, font: fontFamily, size: fontSize10pt, bold: true })
              ]
            })
          );
        }
        
        paragraphs.push(new Paragraph({ text: '' }));
        
        // Déclaration selon le type d'audition
        paragraphs.push(
          new Paragraph({
            children: [
              new TextRun({ text: 'Déclare :', font: fontFamily, size: fontSize10pt, bold: true })
            ]
          })
        );
        
        paragraphs.push(new Paragraph({ text: '' }));
        
        // Texte spécifique selon le type d'audition
        if (typeAudition === 'S3') {
          // S3 - Suspect MAJEUR - Pas privé de liberté
          paragraphs.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `Je n'ai pas été ${prive} de liberté et renonce volontairement, de manière réfléchie et par écrit au droit d'avoir, avant mon audition, une concertation confidentielle avec un avocat de mon choix ou qui m'est désigné et au droit à l'assistance d'un avocat pendant mon audition.`,
                  font: fontFamily,
                  size: fontSize10pt
                })
              ]
            })
          );
          
          paragraphs.push(new Paragraph({ text: '' }));
          
          paragraphs.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `Je n'ai pas reçu de convocation écrite avec énoncé de mes droits, mais vous m'avez communiqué mes droits et vous m'avez informé succinctement des faits sur lesquels je serai ${entendu} avant de procéder à mon audition.`,
                  font: fontFamily,
                  size: fontSize10pt
                })
              ]
            })
          );
          
          paragraphs.push(new Paragraph({ text: '' }));
          
          paragraphs.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `Je suis ${conscient} des conséquences de ma renonciation au droit à une concertation confidentielle préalable avec un avocat et au droit d'assistance d'un avocat pendant mon audition. Je suis ${conscient} que mes déclarations pourront être utilisées comme preuve en justice, et peuvent par conséquence être utilisées contre moi-même ou à charge d'une autre personne. Je sais que lors de mon audition je peux choisir de faire une déclaration, de répondre aux questions posées ou de me taire et que je ne peux pas être ${contraint} de m'accuser moi-même.`,
                  font: fontFamily,
                  size: fontSize10pt
                })
              ]
            })
          );
          
          paragraphs.push(new Paragraph({ text: '' }));
          
          paragraphs.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `Vous m'avez également informé que je peux révoquer ma renonciation.`,
                  font: fontFamily,
                  size: fontSize10pt
                })
              ]
            })
          );
        } else if (typeAudition === 'S4_prive') {
          // S4 - Suspect MAJEUR - Privé de liberté
          paragraphs.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `J'ai été ${prive} de liberté et renonce volontairement, de manière réfléchie et par écrit au droit d'avoir, avant mon audition, une concertation confidentielle avec un avocat de mon choix ou qui m'est désigné et au droit à l'assistance d'un avocat pendant mon audition.`,
                  font: fontFamily,
                  size: fontSize10pt
                })
              ]
            })
          );
          
          paragraphs.push(new Paragraph({ text: '' }));
          
          paragraphs.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `Vous m'avez communiqué mes droits et vous m'avez informé succinctement des faits sur lesquels je serai ${entendu} avant de procéder à mon audition.`,
                  font: fontFamily,
                  size: fontSize10pt
                })
              ]
            })
          );
          
          paragraphs.push(new Paragraph({ text: '' }));
          
          paragraphs.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `Je suis ${conscient} des conséquences de ma renonciation. Je suis ${conscient} que mes déclarations pourront être utilisées comme preuve en justice, et peuvent par conséquence être utilisées contre moi-même ou à charge d'une autre personne. Je sais que lors de mon audition je peux choisir de faire une déclaration, de répondre aux questions posées ou de me taire et que je ne peux pas être ${contraint} de m'accuser moi-même.`,
                  font: fontFamily,
                  size: fontSize10pt
                })
              ]
            })
          );
          
          paragraphs.push(new Paragraph({ text: '' }));
          
          paragraphs.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `Vous m'avez également informé que je peux révoquer ma renonciation.`,
                  font: fontFamily,
                  size: fontSize10pt
                })
              ]
            })
          );
        } else if (typeAudition === 'S4_inculpe') {
          // S4 - Inculpé - Sous mandat d'arrêt
          paragraphs.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `J'ai été ${prive} de liberté et renonce volontairement, de manière réfléchie et par écrit au droit d'assistance d'un avocat durant mon audition.`,
                  font: fontFamily,
                  size: fontSize10pt
                })
              ]
            })
          );
          
          paragraphs.push(new Paragraph({ text: '' }));
          
          paragraphs.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `Vous m'avez communiqué mes droits et vous m'avez informé succinctement des faits sur lesquels je serai ${entendu} avant de procéder à mon audition.`,
                  font: fontFamily,
                  size: fontSize10pt
                })
              ]
            })
          );
          
          paragraphs.push(new Paragraph({ text: '' }));
          
          paragraphs.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `Je suis ${conscient} des conséquences de ma renonciation au droit à l'assistance par un avocat pendant mon audition. Je suis ${conscient} que mes déclarations pourront être utilisées comme preuve en justice, et peuvent par conséquence être utilisées contre moi-même ou à charge d'une autre personne. Je sais que lors de mon audition je peux choisir de faire une déclaration, de répondre aux questions posées ou de me taire et que je ne peux pas être ${contraint} de m'accuser moi-même.`,
                  font: fontFamily,
                  size: fontSize10pt
                })
              ]
            })
          );
          
          paragraphs.push(new Paragraph({ text: '' }));
          
          paragraphs.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `Vous m'avez également informé que je peux révoquer ma renonciation.`,
                  font: fontFamily,
                  size: fontSize10pt
                })
              ]
            })
          );
        }
        
        return paragraphs;
      };
      
      // Créer le document
      const doc = new Document({
        sections: [{
          properties: {},
          headers: {
            default: buildHeader()
          },
          children: [
            // Titre dans un cadre
            new Table({
              width: {
                size: 100,
                type: WidthType.PERCENTAGE
              },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                left: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                right: { style: BorderStyle.SINGLE, size: 1, color: '000000' }
              },
              rows: [
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          alignment: AlignmentType.CENTER,
                          children: [
                            new TextRun({
                              text: 'DÉCLARATION DE RENONCIATION',
                              font: 'Helvetica',
                              size: 28,
                              bold: true
                            })
                          ]
                        })
                      ]
                    })
                  ]
                })
              ]
            }),
            
            // Espace
            new Paragraph({ text: '' }),
            
            // Corps du document
            ...buildBodyParagraphs(),
            
            // Espace avant signature
            new Paragraph({ text: '' }),
            new Paragraph({ text: '' }),
            
            // Bloc signature
            new Paragraph({
              children: [
                new TextRun({ text: 'Nom et prénom : ', font: fontFamily, size: fontSize10pt }),
                new TextRun({ 
                  text: `${(personneEntendueData.lastname || '').toUpperCase()} ${personneEntendueData.firstname || ''}`, 
                  font: fontFamily, 
                  size: fontSize10pt, 
                  bold: true
                })
              ]
            }),
            
            new Paragraph({ text: '' }),
            
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Signe pour renonciation',
                  font: fontFamily,
                  size: fontSize10pt,
                  italics: true
                })
              ]
            })
          ]
        }]
      });
      
      // Générer et télécharger le fichier
      const blob = await Packer.toBlob(doc);
      const fileName = `${reportNumber}-renonciation-avocat.docx`;
      saveAs(blob, fileName);

      // Log du téléchargement
      try {
        const token = sessionStorage.getItem('authToken');
        const typeAuditionLabel = typesAudition.find(t => t.value === typeAudition)?.label || typeAudition;
        
        await axios.post('/api/appendices/log-download', {
          appendixType: 'AttorneyWaiver',
          appendixTitle: 'Renonciation à un avocat',
          reportNumber: `${report?.number}/${report?.year}`,
          formData: {
            personneEntendue: `${personneEntendueData.lastname?.toUpperCase()} ${personneEntendueData.firstname}`,
            dateHeureRenonciation: dateHeureRenonciation,
            typeAudition: typeAuditionLabel,
            typeAuditionCode: typeAudition
          }
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (logError) {
        console.warn('Erreur lors du log du téléchargement:', logError);
        // Ne pas bloquer le téléchargement en cas d'erreur de log
      }
      
    } catch (error) {
      console.error('Erreur lors de la génération du document:', error);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 0 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Complétez les informations ci-dessous pour générer le formulaire de renonciation à un avocat.
        </Typography>
        
        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Personne entendue */}
          <FormControl fullWidth required>
            <InputLabel id="personne-entendue-label">Personne entendue</InputLabel>
            <Select
              labelId="personne-entendue-label"
              value={personneEntendue}
              onChange={handlePersonneChange}
              label="Personne entendue"
            >
              {personnesPhysiques.map((personne, index) => (
                <MenuItem key={index} value={personne.value}>
                  {personne.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Date et heure de la renonciation */}
          <TextField
            fullWidth
            required
            label="Date et heure de la renonciation"
            type="datetime-local"
            value={dateHeureRenonciation}
            onChange={(e) => setDateHeureRenonciation(e.target.value)}
            InputLabelProps={{
              shrink: true
            }}
          />
          
          {/* Type d'audition */}
          <FormControl fullWidth required>
            <InputLabel id="type-audition-label">Type d'audition</InputLabel>
            <Select
              labelId="type-audition-label"
              value={typeAudition}
              onChange={(e) => setTypeAudition(e.target.value)}
              label="Type d'audition"
            >
              {typesAudition.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Bouton de génération */}
          {isFormComplete && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<DescriptionIcon />}
                onClick={handleGenerate}
                sx={{
                  minWidth: 250,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
              >
                Générer le document
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default AttorneyWaiver;
